import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../store/AuthContext";
import {
  CheckCircle2,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
  XCircle,
} from "lucide-react";
import { PageHeader } from "../../components/common/PageHeader";
import { StatCard } from "../../components/common/StatCard";
import { StatusBadge } from "../../components/common/StatusBadge";
import { RestaurantSettlementService } from "../../services";
import { currency, dateTime } from "../../utils/format";
import { currentBusinessDate } from "../../utils/businessDay";
import { businessDateTimeValue } from "../../utils/sriLankaTime";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const PRIMARY_BUTTON_CLASS =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-navy-800 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-navy-900 disabled:cursor-not-allowed disabled:opacity-60";

const ACCENT_BUTTON_CLASS =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-gold-500 px-5 py-2.5 text-sm font-semibold text-navy-900 transition hover:bg-gold-400 disabled:cursor-not-allowed disabled:opacity-60";

const OUTLINE_BUTTON_CLASS =
  "inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60";

const money = (value) => currency(Number(value || 0));

const getInitialFilters = () => {
  const today = currentBusinessDate();

  return {
    fromDate: today,
    toDate: today,
    search: "",
  };
};

const defaultExpenseForm = {
  descriptionType: "Food Purchase",
  customDescription: "",
  amount: "",
  receiptUrl: "",
};

const getExpenseDescription = (form) => {
  if (form.descriptionType === "Other") {
    return form.customDescription.trim();
  }

  return form.descriptionType;
};

const Dialog = ({ dialog, loading, onClose }) => {
  if (!dialog.open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/50 p-0 sm:items-center sm:p-4">
      <div className="w-full rounded-t-2xl bg-white shadow-2xl sm:max-w-md sm:rounded-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-5">
          <div>
            <h3 className="font-display text-lg font-semibold text-slate-900">
              {dialog.title}
            </h3>
            {dialog.subtitle && (
              <p className="mt-1 text-sm text-slate-500">{dialog.subtitle}</p>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5">{dialog.body || dialog.message}</div>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-100 p-5 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className={OUTLINE_BUTTON_CLASS}
          >
            {dialog.cancelLabel || "Close"}
          </button>

          {dialog.onConfirm && (
            <button
              type="button"
              onClick={dialog.onConfirm}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {dialog.confirmLabel || "Confirm"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Pagination = ({
  totalItems,
  page,
  pageSize,
  setPage,
  setPageSize,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const start = totalItems === 0 ? 0 : page * pageSize + 1;
  const end = Math.min(totalItems, (page + 1) * pageSize);

  const canPrevious = page > 0;
  const canNext = page < totalPages - 1;

  return (
    <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <span className="text-slate-500">Rows per page</span>

        <select
          value={pageSize}
          onChange={(event) => {
            setPageSize(Number(event.target.value));
            setPage(0);
          }}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none"
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>

        <span className="text-slate-500">
          {start}–{end} of {totalItems}
        </span>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => setPage((prev) => Math.max(0, prev - 1))}
          disabled={!canPrevious}
          className="rounded-lg border border-slate-200 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Previous
        </button>

        <span className="px-2 text-slate-700">
          Page {page + 1} / {totalPages}
        </span>

        <button
          type="button"
          onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
          disabled={!canNext}
          className="rounded-lg border border-slate-200 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export const RestaurantExpensesPage = () => {
  const [searchParams] = useSearchParams();
  const { role } = useAuth();
  const businessDate = searchParams.get("businessDate") || currentBusinessDate();
  const readOnly = role === "RESTAURANT";
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);

  const [summaryLoading, setSummaryLoading] = useState(true);
  const [expenseLoading, setExpenseLoading] = useState(true);
  const [actionLoadingKey, setActionLoadingKey] = useState("");

  const [expenseForm, setExpenseForm] = useState(defaultExpenseForm);
  const [filters, setFilters] = useState(getInitialFilters);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [dialog, setDialog] = useState({
    open: false,
  });

  const closeDialog = () => {
    if (actionLoadingKey) {
      return;
    }

    setDialog({ open: false });
  };

  const showMessage = (title, message, type = "info") => {
    const isError = type === "error";

    setDialog({
      open: true,
      title,
      body: (
        <div
          className={`flex gap-3 rounded-lg border p-3 text-sm ${
            isError
              ? "border-rose-200 bg-rose-50 text-rose-700"
              : "border-emerald-200 bg-emerald-50 text-emerald-700"
          }`}
        >
          {isError ? (
            <XCircle className="h-5 w-5 shrink-0" />
          ) : (
            <CheckCircle2 className="h-5 w-5 shrink-0" />
          )}
          <p>{message}</p>
        </div>
      ),
      cancelLabel: "OK",
    });
  };

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);

    try {
      const data = await RestaurantSettlementService.current(businessDate);
      setSummary(data || null);
    } finally {
      setSummaryLoading(false);
    }
  }, [businessDate]);

  const loadExpenses = useCallback(async () => {
    setExpenseLoading(true);

    try {
      const data = await RestaurantSettlementService.expenses({ ...filters, businessDate });
      setExpenses(data || []);
    } finally {
      setExpenseLoading(false);
    }
  }, [filters, businessDate]);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadSummary(), loadExpenses()]);
  }, [loadSummary, loadExpenses]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  const addExpense = async () => {
    const description = getExpenseDescription(expenseForm);

    if (!description) {
      showMessage(
        "Description required",
        "Please enter a description for the restaurant expense.",
        "error"
      );
      return;
    }

    const amount = Number(expenseForm.amount || 0);

    if (!amount || amount <= 0) {
      showMessage(
        "Invalid amount",
        "Restaurant expense amount must be greater than zero.",
        "error"
      );
      return;
    }

    setActionLoadingKey("add-expense");

    try {
      await RestaurantSettlementService.addExpense({
        expenseScope: "RESTAURANT",
        amount,
        description,
        receiptUrl: expenseForm.receiptUrl?.trim() || null,
        businessDate,
      });

      setExpenseForm(defaultExpenseForm);

      showMessage(
        "Expense added",
        "Restaurant expense was added successfully.",
        "success"
      );

      await refreshAll();
    } catch (error) {
      showMessage(
        "Expense add failed",
        error?.response?.data?.message ||
          error?.message ||
          "Could not add restaurant expense.",
        "error"
      );
    } finally {
      setActionLoadingKey("");
    }
  };

  const isExpenseLocked = (expense) => {
    if (!summary?.periodStart) {
      return false;
    }

    return businessDateTimeValue(expense.expenseDateTime) < businessDateTimeValue(summary.periodStart);
  };

  const openVoidExpenseDialog = (expense) => {
    let reason = "";

    setDialog({
      open: true,
      title: "Void Restaurant Expense",
      subtitle: "Use this only for incorrect expense entries.",
      body: (
        <div className="space-y-4">
          <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm">
            <div className="flex justify-between gap-3">
              <span className="text-slate-500">Description</span>
              <span className="font-medium text-slate-900">
                {expense.description}
              </span>
            </div>
            <div className="mt-2 flex justify-between gap-3">
              <span className="text-slate-500">Amount</span>
              <span className="font-semibold text-slate-900">
                {money(expense.amount)}
              </span>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700">
              Void reason
            </label>
            <textarea
              rows={3}
              onChange={(event) => {
                reason = event.target.value;
              }}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-100"
              placeholder="Reason for voiding"
            />
          </div>
        </div>
      ),
      confirmLabel: "Void Expense",
      onConfirm: async () => {
        if (!reason.trim()) {
          showMessage(
            "Void reason required",
            "Please enter a reason before voiding the expense.",
            "error"
          );
          return;
        }

        setActionLoadingKey(`void-${expense.expenseId}`);

        try {
          await RestaurantSettlementService.voidExpense(
            expense.expenseId,
            reason.trim()
          );

          setDialog({ open: false });

          showMessage(
            "Expense voided",
            "Restaurant expense was voided successfully.",
            "success"
          );

          await refreshAll();
        } catch (error) {
          showMessage(
            "Void failed",
            error?.response?.data?.message ||
              error?.message ||
              "Could not void restaurant expense.",
            "error"
          );
        } finally {
          setActionLoadingKey("");
        }
      },
      cancelLabel: "Close",
    });
  };

  const filteredExpenses = useMemo(() => {
    const search = filters.search.trim().toLowerCase();

    return (expenses || []).filter((expense) => {
      if (!search) {
        return true;
      }

      const searchable = [
        expense.description,
        expense.status,
        expense.amount,
        expense.receiptUrl,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(search);
    });
  }, [expenses, filters.search]);

  useEffect(() => {
    setPage(0);
  }, [filters]);

  const pagedExpenses = useMemo(() => {
    const start = page * pageSize;
    return filteredExpenses.slice(start, start + pageSize);
  }, [filteredExpenses, page, pageSize]);

  const activeTotal = useMemo(
    () =>
      filteredExpenses
        .filter((expense) => expense.status === "ACTIVE")
        .reduce((sum, expense) => sum + Number(expense.amount || 0), 0),
    [filteredExpenses]
  );

  return (
    <div>
      <PageHeader
        title="Restaurant Expenses"
        subtitle="Add, view, and void restaurant expenses. Expenses reduce Owner Settlement only."
        actions={
          <button
            type="button"
            onClick={refreshAll}
            disabled={summaryLoading || expenseLoading}
            className={PRIMARY_BUTTON_CLASS}
          >
            {summaryLoading || expenseLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </button>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          label="Active Expenses"
          value={summaryLoading ? "…" : money(summary?.restaurantExpenses)}
          icon="Receipt"
          tone="rose"
        />

        <StatCard
          label="Filtered Active Total"
          value={expenseLoading ? "…" : money(activeTotal)}
          icon="Banknote"
          tone="gold"
        />

        <StatCard
          label="Owner Payable"
          value={summaryLoading ? "…" : money(summary?.ownerPayable)}
          icon="BadgeDollarSign"
          tone="navy"
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-[1fr_420px]">
        <div className="bms-card p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h3 className="font-display text-lg font-semibold">
                Expense History
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                Void is disabled once an expense is included in a pending or confirmed Owner Settlement.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[150px_150px_240px]">
              <input
                type="date"
                value={filters.fromDate}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    fromDate: event.target.value,
                  }))
                }
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none"
              />

              <input
                type="date"
                value={filters.toDate}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    toDate: event.target.value,
                  }))
                }
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none"
              />

              <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
                <Search className="h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(event) =>
                    setFilters((prev) => ({
                      ...prev,
                      search: event.target.value,
                    }))
                  }
                  className="w-full bg-transparent text-sm outline-none"
                  placeholder="Search"
                />
              </div>
            </div>
          </div>

          <div className="mt-5 overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-500">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-500">
                    Description
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-slate-500">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-500">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-slate-500">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {pagedExpenses.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-8 text-center text-slate-500"
                    >
                      No restaurant expenses found.
                    </td>
                  </tr>
                ) : (
                  pagedExpenses.map((expense) => {
                    const locked = isExpenseLocked(expense);

                    return (
                      <tr key={expense.expenseId}>
                        <td className="px-4 py-3 text-slate-700">
                          {dateTime(expense.expenseDateTime)}
                        </td>

                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">
                            {expense.description}
                          </div>

                          {expense.receiptUrl && (
                            <a
                              href={expense.receiptUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs font-medium text-navy-700 underline"
                            >
                              View receipt
                            </a>
                          )}

                          {expense.voidReason && (
                            <div className="mt-1 text-xs text-rose-600">
                              Void reason: {expense.voidReason}
                            </div>
                          )}

                          {locked && expense.status === "ACTIVE" && (
                            <div className="mt-1 text-xs text-amber-700">
                              Included in Owner Settlement
                            </div>
                          )}
                        </td>

                        <td className="px-4 py-3 text-right font-semibold">
                          {money(expense.amount)}
                        </td>

                        <td className="px-4 py-3">
                          <StatusBadge value={expense.status} />
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex justify-end">
                            {!readOnly && expense.status === "ACTIVE" ? (
                              <button type="button" onClick={() => openVoidExpenseDialog(expense)} disabled={locked} title={locked ? "This expense is already included in an Owner Settlement" : "Void expense"} className="inline-flex items-center gap-1 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:bg-slate-300"><Trash2 className="h-3 w-3" />Void</button>
                            ) : expense.status !== "ACTIVE" ? (
                              <span className="text-xs text-slate-400">-</span>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            totalItems={filteredExpenses.length}
            page={page}
            pageSize={pageSize}
            setPage={setPage}
            setPageSize={setPageSize}
          />
        </div>

        {!readOnly && <div className="bms-card p-5">
          <h3 className="font-display text-lg font-semibold">
            Add Restaurant Expense
          </h3>

          <div className="mt-4 space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">
                Expense Type
              </label>
              <select
                value={expenseForm.descriptionType}
                onChange={(event) =>
                  setExpenseForm((prev) => ({
                    ...prev,
                    descriptionType: event.target.value,
                  }))
                }
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-navy-700 focus:ring-2 focus:ring-navy-100"
              >
                <option value="Food Purchase">Food Purchase</option>
                <option value="Kitchen Items">Kitchen Items</option>
                <option value="Transport">Transport</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {expenseForm.descriptionType === "Other" && (
              <div>
                <label className="text-sm font-medium text-slate-700">
                  Description
                </label>
                <textarea
                  rows={3}
                  value={expenseForm.customDescription}
                  onChange={(event) =>
                    setExpenseForm((prev) => ({
                      ...prev,
                      customDescription: event.target.value,
                    }))
                  }
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-navy-700 focus:ring-2 focus:ring-navy-100"
                  placeholder="Enter expense description"
                />
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-slate-700">
                Amount
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={expenseForm.amount}
                onChange={(event) =>
                  setExpenseForm((prev) => ({
                    ...prev,
                    amount: event.target.value,
                  }))
                }
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-navy-700 focus:ring-2 focus:ring-navy-100"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">
                Receipt URL optional
              </label>
              <input
                type="text"
                value={expenseForm.receiptUrl}
                onChange={(event) =>
                  setExpenseForm((prev) => ({
                    ...prev,
                    receiptUrl: event.target.value,
                  }))
                }
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-navy-700 focus:ring-2 focus:ring-navy-100"
                placeholder="Paste invoice image URL"
              />
            </div>

            <button
              type="button"
              onClick={addExpense}
              disabled={actionLoadingKey === "add-expense"}
              className={ACCENT_BUTTON_CLASS + " w-full"}
            >
              {actionLoadingKey === "add-expense" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Add Expense
            </button>
          </div>
        </div>}
      </div>

      <Dialog
        dialog={dialog}
        loading={Boolean(actionLoadingKey)}
        onClose={closeDialog}
      />
    </div>
  );
};
